import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import {
  createPublicApiKey,
  deletePublicApiWebhook,
  getPublicApiChannels,
  getPublicApiKeys,
  getPublicApiWebhooks,
  PublicApiScope,
  PublicApiWebhookEvent,
  revokePublicApiKey,
  rotatePublicApiKey,
  upsertPublicApiWebhook,
} from '@/shared/api/public-api';
import { useNotifications } from '@/shared/ui/Notifications';

const PUBLIC_API_QUERY_KEY = 'public-api';

export const usePublicApiKeys = () =>
  useQuery({
    queryKey: [PUBLIC_API_QUERY_KEY, 'keys'],
    queryFn: getPublicApiKeys,
  });

export const usePublicApiChannels = () =>
  useQuery({
    queryKey: [PUBLIC_API_QUERY_KEY, 'channels'],
    queryFn: getPublicApiChannels,
  });

export const usePublicApiWebhooks = (channelId?: string) =>
  useQuery({
    queryKey: [PUBLIC_API_QUERY_KEY, 'webhooks', channelId],
    queryFn: () => getPublicApiWebhooks(channelId ?? ''),
    enabled: Boolean(channelId),
  });

export const useCreatePublicApiKey = () => {
  const queryClient = useQueryClient();
  const { addNotification } = useNotifications();

  return useMutation({
    mutationFn: (input: { name: string; scopes: PublicApiScope[] }) =>
      createPublicApiKey(input),
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: [PUBLIC_API_QUERY_KEY, 'keys'],
      }),
    onError: (error) => {
      addNotification({ type: 'error', title: error.message });
    },
  });
};

export const useRevokePublicApiKey = () => {
  const queryClient = useQueryClient();
  const { addNotification } = useNotifications();

  return useMutation({
    mutationFn: revokePublicApiKey,
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: [PUBLIC_API_QUERY_KEY, 'keys'],
      }),
    onError: (error) => {
      addNotification({ type: 'error', title: error.message });
    },
  });
};

export const useRotatePublicApiKey = () => {
  const queryClient = useQueryClient();
  const { addNotification } = useNotifications();

  return useMutation({
    mutationFn: rotatePublicApiKey,
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: [PUBLIC_API_QUERY_KEY, 'keys'],
      }),
    onError: (error) => {
      addNotification({ type: 'error', title: error.message });
    },
  });
};

export const useUpsertPublicApiWebhook = () => {
  const queryClient = useQueryClient();
  const { addNotification } = useNotifications();

  return useMutation({
    mutationFn: (input: {
      channelId: string;
      url: string;
      events: PublicApiWebhookEvent[];
    }) => upsertPublicApiWebhook(input),
    onSuccess: (_data, variables) =>
      queryClient.invalidateQueries({
        queryKey: [PUBLIC_API_QUERY_KEY, 'webhooks', variables.channelId],
      }),
    onError: (error) => {
      addNotification({ type: 'error', title: error.message });
    },
  });
};

export const useDeletePublicApiWebhook = () => {
  const queryClient = useQueryClient();
  const { addNotification } = useNotifications();

  return useMutation({
    mutationFn: deletePublicApiWebhook,
    onSuccess: (_data, variables) =>
      queryClient.invalidateQueries({
        queryKey: [PUBLIC_API_QUERY_KEY, 'webhooks', variables.channelId],
      }),
    onError: (error) => {
      addNotification({ type: 'error', title: error.message });
    },
  });
};
